import { j as _typeof, k as _createForOfIteratorHelper, e as _objectSpread2, _ as _slicedToArray } from './_rollupPluginBabelHelpers-f5c57928.js';
import React__default, { useState } from 'react';
import PropTypes from 'prop-types';
import { a as amenityShape } from './PropTypes-9aad034b.js';
import Icon from './Icon.js';
import Modal from './Modal.js';
import 'moment';
import './_commonjsHelpers-97e6d7b1.js';
import 'react-dom';
import './ModalHeader-daaa2bf6.js';
import './Title.js';
import './tslib.es6-2748fcb6.js';

/**
 * Creates an object composed of keys generated from the results of running each element of the collection through the iteratee function.
 * The corresponding value of each key is the number of times the key was returned by the iteratee function.
 *
 * @param {Array|Object} collection - The collection to iterate over.
 * @param {Function|String} iteratee - The iteratee function or property name to generate the key.
 * @returns {Object} - The composed object of keys and their respective counts.
 * @throws {TypeError} - If the collection is not an array or an object, or if the iteratee is not a function or a string.
 */
function countBy(collection, iteratee) {
  if (!Array.isArray(collection) && _typeof(collection) !== 'object') {
    throw new TypeError('Collection must be an array or an object');
  }
  if (typeof iteratee !== 'function' && typeof iteratee !== 'string') {
    throw new TypeError('Iteratee must be a function or a string');
  }
  var result = {};
  var isArray = Array.isArray(collection);
  var iterateeFn = typeof iteratee === 'string' ? function (item) {
    return item[iteratee];
  } : iteratee;
  if (isArray) {
    collection.forEach(function (item) {
      var key = iterateeFn(item);
      result[key] = (result[key] || 0) + 1;
    });
  } else {
    for (var key in collection) {
      if (collection.hasOwnProperty(key)) {
        var transformedKey = iterateeFn(collection[key]);
        result[transformedKey] = (result[transformedKey] || 0) + 1;
      }
    }
  }
  return result;
}

/**
 * Returns a new array with unique elements based on the provided iteratee function or property name.
 *
 * @param {Array} array - The array to process.
 * @param {Function|string} iteratee - The iteratee function or property name to determine uniqueness.
 * @returns {Array} - A new array with unique elements.
 * @throws {TypeError} - If the first argument is not an array or if the iteratee is not a function or a string.
 */
function uniqBy(array, iteratee) {
  if (!Array.isArray(array)) {
    throw new TypeError('First argument must be an array');
  }
  if (typeof iteratee !== 'function' && typeof iteratee !== 'string') {
    throw new TypeError('Iteratee must be a function or a string');
  }
  var seen = new Map();
  var result = [];
  var getKey = typeof iteratee === 'function' ? function (item) {
    return iteratee(item);
  } : function (item) {
    return _typeof(item) === 'object' && item !== null && iteratee in item ? item[iteratee] : undefined;
  };
  var _iterator = _createForOfIteratorHelper(array),
    _step;
  try {
    for (_iterator.s(); !(_step = _iterator.n()).done;) {
      var item = _step.value;
      var key = getKey(item);
      if (key !== undefined && !seen.has(key)) {
        seen.set(key, true);
        result.push(item);
      }
    }
  } catch (err) {
    _iterator.e(err);
  } finally {
    _iterator.f();
  }
  return result;
}

function Amenity(props) {
  var icon = props.icon,
    defaultIcon = props.defaultIcon,
    text = props.text,
    iconSize = props.iconSize,
    className = props.className,
    total = props.total;
  return /*#__PURE__*/React__default.createElement("div", {
    className: "bf-amenity-wrapper ".concat(className)
  }, icon ? /*#__PURE__*/React__default.createElement("span", {
    className: "bf-amenity-icon-wrapper"
  }, /*#__PURE__*/React__default.createElement(Icon, {
    name: icon || defaultIcon,
    size: iconSize,
    className: "bf-amenity-icon"
  })) : null, total && total > 1 ? /*#__PURE__*/React__default.createElement("span", {
    className: "bf-amenity-total"
  }, total, "x") : null, /*#__PURE__*/React__default.createElement("span", {
    className: "bf-amenity-name"
  }, text));
}
Amenity.defaultProps = {
  icon: '',
  defaultIcon: 'bf-icon-check',
  iconSize: 'normal',
  className: '',
  total: null
};
Amenity.propTypes = {
  /**
  Amenity icon
   */
  icon: PropTypes.string,
  /**
  Amenity default icon
   */
  defaultIcon: PropTypes.string,
  /**
  Amenity name
   */
  text: PropTypes.string.isRequired,
  /**
  Amenity icon size
   */
  iconSize: PropTypes.string,
  /**
  Custom className
   */
  className: PropTypes.string,
  /**
   * Total number of identical amenities
   */
  total: PropTypes.number
};

function AmenitiesGrid(props) {
  var amenities = props.amenities,
    className = props.className,
    iconSize = props.iconSize;
  return /*#__PURE__*/React__default.createElement("div", {
    className: "bf-amenities-grid-wrapper ".concat(className)
  }, amenities.map(function (amenity, index) {
    var key = "AMENITY_".concat(index);
    return /*#__PURE__*/React__default.createElement(Amenity, {
      icon: amenity.icon,
      defaultIcon: amenity.defaultIcon,
      text: amenity.text,
      iconSize: iconSize,
      key: key,
      total: amenity.total
    });
  }));
}
AmenitiesGrid.defaultProps = {
  className: '',
  iconSize: 'large'
};
AmenitiesGrid.propTypes = {
  /**
  Amenities array
   */
  amenities: PropTypes.arrayOf(amenityShape).isRequired,
  /**
  Amenities icons size
   */
  iconSize: PropTypes.string,
  /**
  Custom className
   */
  className: PropTypes.string
};

function AmenitiesModal(props) {
  var amenities = props.amenities,
    trigger = props.trigger,
    iconSize = props.iconSize,
    labels = props.labels,
    isQA = props.isQA;
  var dataTestId = isQA ? 'bf-amenities-show-more-link' : null;
  return /*#__PURE__*/React__default.createElement(Modal, {
    trigger: trigger(labels.showAll, dataTestId),
    title: labels.amenities,
    content: /*#__PURE__*/React__default.createElement(AmenitiesGrid, {
      className: "bf-amenities-modal-wrapper",
      amenities: amenities,
      iconSize: iconSize
    }),
    size: "small"
  });
}
AmenitiesModal.defaultProps = {
  trigger: function trigger(text, dataTestId) {
    return /*#__PURE__*/React__default.createElement("a", {
      className: "bf-amenities-show-more-link",
      "data-test-id": dataTestId
    }, text);
  },
  iconSize: 'large',
  labels: {
    showAll: 'Show all amenities',
    amenities: 'Amenities'
  },
  isQA: false
};
AmenitiesModal.propTypes = {
  /**
  Amenities array
   */
  amenities: PropTypes.arrayOf(amenityShape).isRequired,
  /**
  Modal trigger
   */
  trigger: PropTypes.oneOfType([PropTypes.number, PropTypes.string, PropTypes.func]),
  /**
  Amenities icons size
   */
  iconSize: PropTypes.string,
  // eslint-disable-next-line react/forbid-prop-types
  labels: PropTypes.object,
  isQA: PropTypes.bool
};

function AmenitiesList(props) {
  var amenities = props.amenities,
    maxQuantity = props.maxQuantity,
    showMoreInline = props.showMoreInline,
    className = props.className,
    iconSize = props.iconSize,
    labels = props.labels,
    isQA = props.isQA;
  var totals = countBy(amenities, 'code');
  var uniqueAmenities = uniqBy(amenities, 'code').map(function (item) {
    return _objectSpread2(_objectSpread2({}, item), {}, {
      total: totals[item.code]
    });
  });
  var reducedAmenities = uniqueAmenities.slice(0, maxQuantity);
  var expandedAmenities = uniqueAmenities.slice(maxQuantity, uniqueAmenities.length);
  var _useState = useState(false),
    _useState2 = _slicedToArray(_useState, 2),
    showMoreActive = _useState2[0],
    setShowMoreActive = _useState2[1];
  var dataTestId = isQA ? 'bf-amenities-show-more-link' : null;
  return /*#__PURE__*/React__default.createElement("div", {
    className: "bf-amenities-list-wrapper ".concat(className)
  }, reducedAmenities.map(function (amenity, index) {
    var key = "AMENITY_".concat(index);
    return /*#__PURE__*/React__default.createElement(Amenity, {
      icon: amenity.icon,
      defaultIcon: amenity.defaultIcon,
      text: amenity.text,
      iconSize: iconSize,
      key: key,
      total: amenity.total
    });
  }), reducedAmenities.length < uniqueAmenities.length && (showMoreInline ? /*#__PURE__*/React__default.createElement(React__default.Fragment, null, showMoreActive ? expandedAmenities.map(function (amenity, index) {
    var key = "AMENITY_".concat(index);
    return /*#__PURE__*/React__default.createElement(Amenity, {
      icon: amenity.icon,
      defaultIcon: amenity.defaultIcon,
      text: amenity.text,
      iconSize: iconSize,
      key: key,
      total: amenity.total
    });
  }) : null, /*#__PURE__*/React__default.createElement("a", {
    className: "bf-amenities-show-more-link",
    "data-test-id": dataTestId,
    onClick: function onClick() {
      setShowMoreActive(!showMoreActive);
    }
  }, !showMoreActive ? labels.showMore : labels.showLess)) : /*#__PURE__*/React__default.createElement(AmenitiesModal, {
    amenities: uniqueAmenities,
    labels: {
      showAll: labels.showAll,
      amenities: labels.amenities
    },
    isQA: isQA
  })));
}
AmenitiesList.defaultProps = {
  maxQuantity: 5,
  showMoreInline: false,
  iconSize: '',
  className: '',
  labels: {
    showMore: 'Show more',
    showLess: 'Show less',
    showAll: 'Show all amenities',
    amenities: 'Amenities'
  },
  isQA: false
};
AmenitiesList.propTypes = {
  /**
  Amenities array
   */
  amenities: PropTypes.arrayOf(amenityShape).isRequired,
  /**
  Amenities to show
   */
  maxQuantity: PropTypes.number,
  /**
  Amenities icons size
   */
  iconSize: PropTypes.string,
  /**
  Show hidden amenities inline and not with a modal
   */
  showMoreInline: PropTypes.bool,
  /**
  Custom className
   */
  className: PropTypes.string,
  // eslint-disable-next-line react/forbid-prop-types
  labels: PropTypes.object,
  isQA: PropTypes.bool
};
AmenitiesList.AmenitiesGrid = AmenitiesGrid;

export default AmenitiesList;
